import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { get } from 'lodash';
import moment from 'moment-timezone';
// import { useNavigate } from 'react-router-dom';

import {
  SdfAlert,
  SdfBox,
  SdfButton,
  SdfExpandableBox,
  SdfFocusPane,
  SdfIcon,
  SdfPageHeaderLayout,
  SdfPromoBox
} from "@synerg/react-components";;
import CreateCertificateModal from './createCertificateModal';
import ViewCertificateModal from './viewCertificateModal';

export default function CertificateView({
                                          orgName, email, setIsBusy, handleResponse, fetchCertificates, getCertificate, onDelete, onUpload, retry, generateCert, submitReq, setShowNotificationMenu, history }) {

  // const history = useNavigate();
  const fileInputRef = useRef(null);
  const [certificates, setCertificates] = useState([]);
  const [selectedCert, setSelectedCert] = useState(false);
  const [isStepOpen, setIsStepOpen] = useState({ 0: true });
  const [showCreateCertificateModal, setShowCreateCertificateModal] = useState(history?.location?.hash === '#generate');
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [deleteCert, setDeleteCert] = useState({});

  const fetchAllCertificates = async () => {
    setIsBusy(true);
    try {
      const response = await fetchCertificates();
      const data = get(response, 'data.certificates', []);
      setCertificates(data);
      setIsBusy(false);
    } catch (e) {
      setIsBusy(false);
      const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching certificates. Please try again.');
      handleResponse(errorMessage);
    }
  };

  const onStepChanged = (step, status) => {
    setIsStepOpen((s) => ({
      ...s,
      ...({ [step]: status === "open" })
    }));
  };

  const getCertificateDetail = async (certID) => {
    try {
      const response = await getCertificate(certID);
      const data = get(response, 'data.certificateInfo', null);
      return data;
    } catch (e) {
      const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching certificate. Please try again.');
      handleResponse(errorMessage);
      setSelectedCert(null);
      throw new Error(errorMessage);
    }
  };

  const onDownloadClick = async (cert, index) => {
    try {
      setIsBusy(true);
      const { certificate } = await getCertificateDetail(cert.id);
      const hiddenElement = document.createElement('a');
      hiddenElement.href = `data:application/x-pem-file;charset=utf-8,${encodeURIComponent(certificate)}`;
      hiddenElement.target = '_blank';
      hiddenElement.download = `myPublicCertificate_${cert.commonName}.pem`;
      hiddenElement.click();
      setIsBusy(false);
    } catch (error) {
      const errorMessage = get(error, 'response.data.message', 'Something went wrong while downloading certificate. Please try again.');
      handleResponse(errorMessage);
      setIsBusy(false);
    }
  };

  const onDeleteCertificateConfirm = async (cert) => {
    setOpenConfirmation(false);
    setIsBusy(true);
    try {
      const response = await onDelete(cert.id);
      const success = get(response, 'data.success', false);
      if (success) {
        setCertificates(certificates => (certificates.filter(c => {
          if (c.id !== cert.id) {
            return c;
          }
        })));
        setIsStepOpen({ 0: true });
        handleResponse(`Successfully deleted certificate with name ${cert.commonName}`, 'success');
      } else {
        handleResponse('Something went wrong while deleting certificate. Please try again.');
      }
      setIsBusy(false);
      setDeleteCert({});
    } catch (e) {
      const errorMessage = get(e, 'response.data.message', 'Something went wrong while deleting certificate. Please try again.');
      handleResponse(errorMessage);
      setIsBusy(false);
      setDeleteCert({});
    }
  };

  const onDeleteCertificate = (cert) => {
    setOpenConfirmation(true);
    setDeleteCert(cert);
  };

  const requestForCertificate = () => {
    setShowCreateCertificateModal(true);
  };

  const closeCreateCertModal = async (refresh) => {
    setShowCreateCertificateModal(false);
    if (refresh) {
      await fetchAllCertificates();
    }
  };

  const uploadToServer = async event => {
    setIsBusy(true);
    const uploadFile = get(event, 'target.files[0]');
    if (uploadFile) {
      const { name: uploadFileName } = uploadFile;
      const formData = new FormData();
      if (uploadFile) {
        formData.append('certificateFile', uploadFile, uploadFileName);
      }
      try {
        await onUpload(formData);
        handleResponse(`Successfully uploaded certificate`, 'success');
        await fetchAllCertificates();
        setIsBusy(false);
      } catch (e) {
        setIsBusy(false);
        const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to upload certificate. Please try again.');
        handleResponse(errorMessage);
      }
      fileInputRef.current.value = null;
    }
  };

  const onCheckStatus = async (certId) => {
    setIsBusy(true);
    const cert = await getCertificateDetail(certId);
    if (cert.status !== 'processing') {
      await fetchAllCertificates();
    }
    setIsBusy(false);
  };

  const onRetry = async (certId) => {
    setIsBusy(true);
    try {
      const { data: { success } } = await retry(certId);
      if (success) {
        await fetchAllCertificates();
      } else {
        handleResponse('Something went wrong when trying retry. Please try again.');
      }
      setIsBusy(false);

    } catch (e) {
      const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying retry. Please try again');
      handleResponse(errorMessage);
      setIsBusy(false)
    }
    return true;
  };

  useEffect(() => {
    fetchAllCertificates()
  }, []);

  function ShowCertInfo({ cert }) {
    const expiresIn = moment.tz(cert.expires, 'America/New_York').diff(moment.tz(new Date(), 'America/New_York'), 'days');
    const expiryInfo = { message: 'Certificate expiry date unavailable', alertType: 'warning' };
    if (cert.status && cert.status === 'failed') {
      expiryInfo.message = `Generation Failed. Please try request again or contact your technical advisor.`;
      expiryInfo.alertType = 'error';
    } else if (cert.status && cert.status === 'processing') {
      expiryInfo.message = `Generating...`;
      expiryInfo.alertType = 'info';
    } else if (expiresIn > 0) {
      expiryInfo.message = `${expiresIn} days remain before certificate expires.`;
    } else if (expiresIn < 0) {
      expiryInfo.alertType = 'error';
      expiryInfo.message = `Your certificate expired ${-expiresIn} days ago. Please request a new certificate.`;
    } else if (expiresIn === 0) {
      expiryInfo.alertType = 'error';
      expiryInfo.message = 'Your certificate expired today. Please request a new certificate.';
    }
    return (<div><SdfIcon icon="alert-warning" /> {expiryInfo.message}</div>);
  };

  const Confirmation = ({ cert }) => {
    return (<SdfFocusPane
      paneType="floating"
      visible={true}
      size="small"
      onSdfDismiss={() => { setOpenConfirmation(false) }}
      hideAcceptButton={true}
      hideDismissButton={true}
      closeable={true}
    >
      <span slot="title">Are you sure?</span>
      <div className="text-body-02 mx-6 text-left">
        <h4>{`You are going to delete certificate named "${cert.commonName}"`}</h4>
      </div>
      <div slot={"custom-buttons"}>
        <SdfButton emphasis={"primary"} onClick={() => onDeleteCertificateConfirm(cert)}>
          Yes
        </SdfButton>
        <SdfButton onClick={() => {
          setOpenConfirmation(false)
        }} className="ml-3">Cancel</SdfButton>
      </div>
    </SdfFocusPane >)
  };

  return (
    <div>
      <div className="px-20 py-10">
        <SdfPageHeaderLayout className="hydrated">
          <span slot="title-content" className="text-heading-08 font-black">Certificates</span>
        </SdfPageHeaderLayout>
        <div className={`grid ${certificates.length > 0 ? 'xl:grid-cols-3 md:grid-cols-1 gap-10' : 'gid-cols-1'}`}>
          {certificates.length > 0 && < div className="xl:col-span-2">
            <SdfBox shape="rounded" variant="bordered" spacing="loose">
              <div className="px-10 pb-10">
                <div className="text-heading-06 font-bold my-5">Certificate management</div>
                {certificates.map((cert, index) => (
                  <div key={cert.id} className="mb-10">
                    <SdfExpandableBox
                      variant="filled"
                      triggerPlacement="after"
                      stroke="light"
                      opened={isStepOpen[index] || false}
                      header={cert.commonName}
                      headingLevel="3"
                      onSdfClose={(e) => { onStepChanged(index, "close") }}
                      onSdfOpen={(e) => { onStepChanged(index, "open") }}
                    >
                      <div className="p-5 pr-20">
                        <div className="mb-5">
                          <label>Generated on</label>
                          <div className="text-heading-05 font-bold mt-2">{moment.tz(cert.createdAt, 'America/New_York').format('MMM DD, YYYY')}</div>
                        </div>
                        <div className="mb-5">
                          <label>Expires on</label>
                          <div className="text-heading-05 font-bold mt-2">{cert.status === "processing" ? "Processing..." : moment.tz(cert.expires, 'America/New_York').format('MMM DD, YYYY')}</div>
                          <div className="text-alert-warning mt-2">
                            <ShowCertInfo cert={cert} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-5">
                          {cert.status === "active" && <>
                            <SdfButton emphasis="primary" onClick={(e) => { setSelectedCert(cert) }}>View</SdfButton>
                            <SdfButton emphasis="secondary" onClick={() => { onDownloadClick(cert) }}>Download</SdfButton>
                          </>}
                          {cert.status === "processing" && <SdfButton emphasis="primary" onClick={(e) => { onCheckStatus(cert.id) }}>Check status</SdfButton>}
                          {cert.status === "failed" && <SdfButton emphasis="primary" onClick={(e) => { onRetry(cert.id) }}>Retry</SdfButton>}
                        </div>
                        <div className="flex mt-5 font-medium"
                             style={{ color: "#c53422", cursor: "pointer" }}
                             onClick={() => { onDeleteCertificate(cert) }}>
                          <SdfIcon icon="action-delete" className="mr-1" />
                          Delete certificate
                        </div>
                      </div>
                    </SdfExpandableBox>
                  </div>))}
                <SdfAlert
                  status='info'
                  size='sm'
                  aria-live="polite"
                  closeable={true}
                  className="sm:col-span-2 md:col-span-1 break-word"
                >
                  <span slot="title">You will be notified before your certificate expires.</span>
                  <div>
                    <p>
                      When your signed certificate is within 60 days of expiration,
                      all individuals who have active status in the Members tab
                      will receive multiple notifications at regular intervals until it has expired.
                      If you would like to stop receiving certificate expiration notices,
                      you may opt-out by adjusting your settings here.
                    </p>
                    <SdfButton variant="text" onClick={() => setShowNotificationMenu(true)} emphasis="tertiary">Opt-out of notifications</SdfButton>
                  </div>
                </SdfAlert>
              </div>
            </SdfBox>
          </div>}
          <div>
            <SdfPromoBox
              variant="bordered"
              layout={certificates.length > 0 ? "vertical" : "horizontal"}
              align="center"
              header="Need a certificate?"
              spacing="loose"
              message="A signed certificate from ADP's Certificate Authority (CA) must be presented in order to fetch an access token from ADP and for every subsequent API request to ADP's API Gateway.
                            A certificate created here will be used across all projects you create in both development and production.
                            Request a certificate below to get started."
              illustrationName="cloud-shield"
            >
              <div className="text-center" slot="cta">
                <SdfButton emphasis="secondary" {...(certificates.length >= 10 && { disabled: true })} onClick={requestForCertificate}>Request certificate</SdfButton>
              </div>
            </SdfPromoBox>
            <div className="mt-10">
              <div>
                <p className="text-center"> Already have a signed certificate and want us to track expiration for you?</p>
                <div className="text-center">
                  <SdfButton {...(certificates.length >= 10 && { disabled: true })} onClick={() => { fileInputRef.current.click() }} variant="text">Upload signed certificate</SdfButton>
                  <input type="file" accept=".pem" ref={fileInputRef} className="hidden" placeholder="Upload a certificate (Limit: 100 MB)" id="certificateFile" name="certificateFile" onChange={uploadToServer} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedCert && <ViewCertificateModal certID={selectedCert.id} setSelectedCert={setSelectedCert} getCertificateDetail={getCertificateDetail} />}
      {showCreateCertificateModal && <CreateCertificateModal orgName={orgName} email={email} closeCreateCertModal={closeCreateCertModal} handleResponse={handleResponse} generateCert={generateCert} submitReq={submitReq} />}
      {openConfirmation && <Confirmation cert={deleteCert} />}
    </div>
  )
}
